import { supabase, handleSupabaseError } from '../lib/supabase';

// Add new function for file upload handling
const uploadInquiryAttachment = async (inquiryId, file) => {
  try {
    const fileExt = file?.name?.split('.')?.pop();
    const fileName = `${Date.now()}_${Math.random()?.toString(36)?.substring(2)}.${fileExt}`;
    const filePath = `inquiry-${inquiryId}/${fileName}`;

    const { data, error } = await supabase?.storage?.from('legal-attachments')?.upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) {
      throw error;
    }

    return { data: { path: filePath, fullPath: data?.path }, error: null };
  } catch (error) {
    return { data: null, error: error?.message };
  }
};

// Add function to get signed URLs for attachments
const getAttachmentSignedUrl = async (filePath, expiresIn = 3600) => {
  try {
    const { data, error } = await supabase?.storage?.from('legal-attachments')?.createSignedUrl(filePath, expiresIn);

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    return { data: null, error: error?.message };
  }
};

// Add function to delete attachment
const deleteAttachment = async (filePath) => {
  try {
    const { error } = await supabase?.storage?.from('legal-attachments')?.remove([filePath]);

    if (error) {
      throw error;
    }

    return { error: null };
  } catch (error) {
    return { error: error?.message };
  }
};

export const inquiryService = {
  // Update createInquiry function to handle attachments
  async createInquiry(inquiryData, attachedFiles = []) {
    try {
      // Ensure urgency is properly set with fallback
      const urgencyValue = inquiryData?.urgency || 'medium';
      
      // Validate urgency against enum values
      if (!['low', 'medium', 'high', 'urgent']?.includes(urgencyValue)) {
        throw new Error('Invalid urgency value. Must be one of: low, medium, high, urgent');
      }

      console.log('Creating inquiry with urgency:', urgencyValue); // Debug log

      // First create the inquiry with all required fields
      const insertData = {
        first_name: inquiryData?.firstName,
        last_name: inquiryData?.lastName,
        email: inquiryData?.email,
        phone: inquiryData?.phone,
        legal_area_id: inquiryData?.legalArea || null,
        urgency: urgencyValue, // Ensure this is always set
        message: inquiryData?.message,
        user_id: inquiryData?.userId || null,
        privacy_consent: Boolean(inquiryData?.privacyConsent),
        newsletter_consent: Boolean(inquiryData?.newsletterConsent)
      };

      console.log('Insert data prepared:', insertData); // Debug log

      const { data: inquiry, error: inquiryError } = await supabase?.from('legal_inquiries')?.insert(insertData)?.select('id')?.single();

      if (inquiryError) {
        console.error('Database insert error:', inquiryError); // Debug log
        throw inquiryError;
      }

      console.log('✅ Inquiry created successfully with ID:', inquiry?.id);

      let attachmentUrls = [];

      // Upload files if any are provided with enhanced error handling
      if (attachedFiles?.length > 0) {
        console.log(`📎 Uploading ${attachedFiles?.length} attachments...`);
        
        for (const file of attachedFiles) {
          try {
            const { data: uploadData, error: uploadError } = await uploadInquiryAttachment(
              inquiry?.id,
              file
            );

            if (uploadError) {
              console.warn(`⚠️ File upload failed for ${file?.name}:`, uploadError);
              // Continue with other files - don't block the inquiry
            } else if (uploadData) {
              attachmentUrls?.push(uploadData?.path);
              console.log(`✅ File uploaded successfully: ${file?.name}`);
            }
          } catch (fileError) {
            console.warn(`⚠️ File processing error for ${file?.name}:`, fileError);
            // Continue with other files
          }
        }

        // Update inquiry with attachment URLs if any were successful
        if (attachmentUrls?.length > 0) {
          console.log(`📎 Updating inquiry with ${attachmentUrls?.length} attachment URLs`);
          
          const { error: updateError } = await supabase?.from('legal_inquiries')?.update({ 
            attachment_urls: attachmentUrls 
          })?.eq('id', inquiry?.id);

          if (updateError) {
            console.warn('Failed to update inquiry with attachment URLs:', updateError);
          } else {
            console.log('✅ Attachment URLs updated successfully');
          }
        }
      }

      // ✅ CRITICAL FIX: Send email notification after successful inquiry creation
      console.log('📧 Attempting to send email notification for inquiry:', inquiry?.id);
      
      try {
        // Get the complete inquiry data with related information for email
        const { data: completeInquiry, error: fetchError } = await supabase
          ?.from('legal_inquiries')
          ?.select(`
            *,
            legal_areas:legal_area_id(name)
          `)
          ?.eq('id', inquiry?.id)
          ?.single();

        if (!fetchError && completeInquiry) {
          console.log('📧 Sending email notification with complete inquiry data');
          
          // Send email notification with detailed error handling
          const emailResult = await this.sendInquiryNotification(completeInquiry);
          
          if (emailResult?.error) {
            console.error('❌ Email notification failed:', emailResult?.error);
            // Check if it's a configuration issue
            if (emailResult?.error?.includes('RESEND_API_KEY')) {
              console.error('🔑 Missing RESEND_API_KEY in Supabase Edge Function environment');
              console.error('💡 Please configure RESEND_API_KEY in your Supabase project settings');
            }
            // Don't throw error - inquiry was created successfully
          } else {
            console.log('✅ Email notification sent successfully:', emailResult?.data);
          }
        } else {
          console.error('❌ Failed to fetch complete inquiry data for email:', fetchError);
        }
      } catch (emailError) {
        console.error('❌ Email sending process failed:', emailError);
        console.error('💡 Common causes: Missing RESEND_API_KEY, Edge Function not deployed, or network issues');
        // Don't throw error - inquiry was created successfully, email failure shouldn't block the process
      }

      console.log('🎉 Inquiry creation process completed successfully');
      return { data: { ...inquiry, attachment_urls: attachmentUrls }, error: null };
    } catch (error) {
      console.error('💥 Critical error in createInquiry:', error);
      return { data: null, error: error?.message };
    }
  },

  // Get inquiries for a specific user
  async getUserInquiries(userId) {
    try {
      const { data, error } = await supabase?.from('legal_inquiries')?.select(`
          *,
          legal_areas:legal_area_id(name),
          assigned_lawyer:assigned_lawyer_id(full_name, email)
        `)?.eq('user_id', userId)?.order('created_at', { ascending: false })

      if (error) throw error
      return { data: data || [], error: null }
    } catch (error) {
      return { data: [], error: handleSupabaseError(error) }
    }
  },

  // Get all inquiries (admin/lawyer view)
  async getAllInquiries(filters = {}) {
    try {
      let query = supabase?.from('legal_inquiries')?.select(`
          *,
          legal_areas:legal_area_id(name),
          assigned_lawyer:assigned_lawyer_id(full_name, email)
        `)

      // Apply filters
      if (filters?.status) {
        query = query?.eq('status', filters?.status)
      }
      
      if (filters?.urgency) {
        query = query?.eq('urgency', filters?.urgency)
      }
      
      if (filters?.legalAreaId) {
        query = query?.eq('legal_area_id', filters?.legalAreaId)
      }

      const { data, error } = await query?.order('created_at', { ascending: false })

      if (error) throw error
      return { data: data || [], error: null }
    } catch (error) {
      return { data: [], error: handleSupabaseError(error) }
    }
  },

  // Update inquiry status
  async updateInquiryStatus(inquiryId, status, assignedLawyerId = null) {
    try {
      const updateData = { 
        status,
        updated_at: new Date()?.toISOString()
      }
      
      if (assignedLawyerId) {
        updateData.assigned_lawyer_id = assignedLawyerId
      }

      const { data, error } = await supabase?.from('legal_inquiries')?.update(updateData)?.eq('id', inquiryId)?.select(`
          *,
          legal_areas:legal_area_id(name),
          assigned_lawyer:assigned_lawyer_id(full_name, email)
        `)

      if (error) throw error
      return { data: data?.[0], error: null }
    } catch (error) {
      return { data: null, error: handleSupabaseError(error) }
    }
  },

  // Delete inquiry
  async deleteInquiry(inquiryId) {
    try {
      const { error } = await supabase?.from('legal_inquiries')?.delete()?.eq('id', inquiryId)

      if (error) throw error
      return { error: null }
    } catch (error) {
      return { error: handleSupabaseError(error) }
    }
  },

  // Enhanced email notification method with better error handling
  async sendInquiryNotification(inquiry) {
    try {
      console.log('📧 Invoking send-inquiry-notification Edge Function...');
      
      const { data, error } = await supabase?.functions?.invoke('send-inquiry-notification', {
        body: {
          inquiryId: inquiry?.id,
          recipientEmail: inquiry?.email,
          recipientName: `${inquiry?.first_name} ${inquiry?.last_name}`,
          legalArea: inquiry?.legal_areas?.name || 'Allgemeine Anfrage',
          urgency: inquiry?.urgency,
          message: inquiry?.message
        }
      });

      if (error) {
        console.error('📧 Edge Function invocation error:', error);
        throw error;
      }

      console.log('📧 Edge Function response:', data);
      return { data, error: null };
    } catch (error) {
      console.error('📧 Email notification error details:', error);
      
      // Provide helpful error messages
      if (error?.message?.includes('RESEND_API_KEY')) {
        return { 
          data: null, 
          error: 'Email service configuration missing. Please contact support.' 
        };
      } else if (error?.message?.includes('Function not found')) {
        return { 
          data: null, 
          error: 'Email service temporarily unavailable. Inquiry was saved successfully.' 
        };
      }
      
      return { data: null, error: error?.message };
    }
  },

  // Get legal areas for form dropdowns
  async getLegalAreas() {
    try {
      const { data, error } = await supabase?.from('legal_areas')?.select('*')?.eq('is_active', true)?.order('name')

      if (error) throw error
      return { data: data || [], error: null }
    } catch (error) {
      return { data: [], error: handleSupabaseError(error) }
    }
  }
};

export { uploadInquiryAttachment, getAttachmentSignedUrl, deleteAttachment };